<script lang='ts'>
    import { onMount, createEventDispatcher, getContext } from "svelte";
    import { addDays } from "date-fns";
    import ActivitiesDashLegacy from "../../ActivitiesDashLegacy.svelte";
    import DatePicker from "../../Common/DatePicker.svelte";
    import NumberInput from "../../Common/NumberInput.svelte";
    import type { MyFitnessStores } from "@/MyFitness/stores";
    import ProgramAchievabilityMessage from "./ProgramAchievabilityMessage.svelte";
    import { LocalDate } from "@/time";
    import type { Writable } from "svelte/store";
    import type { RacePlan } from "@/Common/constants";
    import type { XSSDetails } from "@/Util/utils";
    import type { ProgramAchievability } from "../modules/ProgramAchievability";

    const myFitness = getContext<MyFitnessStores>('myFitness')
    const {userParams} = myFitness

    export let fileData: FormData = null
    export let activityPath: string = undefined
    export let riderWeight = undefined
    export let raceDate: string
    export let raceLogoUrl: string

    // For RaceCard & Assess Modal // TODO: Run assess in modal and pass through instead of the other way around
    export let targetSignature: XSSDetails
    export let programAchievability: ProgramAchievability
    export let plan: Writable<RacePlan>
    export let mpaData
    
    let className = ""
    export {className as class}

    // Form vars
    let minDate = LocalDate.format(new Date()) // Today

    // Tab navigation
    enum Tab {
        Select,
        Library,
        Personal,
        Squad,
        FileUpload,
    }
    let selectedTab = Tab.Select

    const selectButtons = [
        {title: "Analyze a Power File", tab: Tab.FileUpload, description: "Upload a FIT file of a race/ride/KOM that you'd like to achieve.", icon: "fa-cloud"},
        {title: "Race/Event Library", tab: Tab.Library, description: "Choose a race or event from our library to use as your target.", icon: "fa-book-open"},
        {title: "Personal Activities", tab: Tab.Personal, description: "Pick a ride from your past that you'd like to achieve or improve.", icon: "fa-bicycle"},
        // {title: "Squad", tab: Tab.Squad, description: "Lorem ipsum dolor sit amet. Eum ipsa asperiores sit beatae.", icon: "fa-help"},
    ]

    // Submit event
    const dispatch = createEventDispatcher()
    const dispatchSubmit = () => dispatch("select")
    const dispatchAssess = () => dispatch("assess")
    const dispatchChangeOption = () => dispatch("changeOption")

    // File uploader
    //@ts-ignore
    let jq = window.$;
    let raceFileForm: HTMLFormElement
    let fileUploader: any
    onMount(()=>{
        jq('input.race-file').fileuploader({
            limit: 1,
            extensions: ['tcx', 'fit'],
            upload: false,
            inputNameBrackets: true,
            enableApi: true,
            theme: "dragdrop",
            changeInput:
                '<div class="fileuploader-input">' +
                    '<div class="fileuploader-input-inner">' +
                        '<h3 class="fileuploader-input-caption"><span>${captions.feedback}</span></h3>' +
                        '<p>${captions.or}</p>' +
                        '<button type="button" class="fileuploader-input-button bg-xerteal-400"><span>${captions.button}</span></button>' +
                    '</div>' +
                    '<div class="mt-8 text-slate-500">${captions.fileOptions}</div>' +
                '</div>',
            captions: jq.extend(true, {}, jq.fn.fileuploader.languages['en'], {
                feedback: 'Drag and drop files here',
                feedback2: 'Drag and drop files here',
                drop: 'Drag and drop files here',
                or: 'or',
                button: 'Browse files',
                fileOptions: 'Supports FIT files up to 5 MB and TCX files up to 30 MB'
            }),
            onRemove() {
                fileInput.value = null
            }
        })
        fileUploader = jq.fileuploader.getInstance('input.race-file');
    })

    // Form inputs
    let riderWeightInput: HTMLInputElement
    let fileInput: HTMLInputElement
    
    let validationError: string = null
    function validateSubmit(assessOnly: boolean) {
        validationError = null
        fileData = null
        if(selectedTab === Tab.FileUpload) {
            if (!riderWeight || riderWeight <= 0) validationError = "Please input a rider weight greater than 0."
            if (!fileInput.checkValidity()) validationError = "Please ensure you've selected a file."
            if (!raceDate) validationError = "Please a select a date."            
            if (validationError) return
            fileData = new FormData(raceFileForm)
            raceLogoUrl = null
        }

        if (assessOnly) dispatchAssess()
        else dispatchSubmit()
    }

    function returnToSelect() {
        activityPath = null
        fileData = null
        validationError = null
        dispatchChangeOption()
        selectedTab = Tab.Select
    }

    const onSelectAutoFire = () => {
        if (selectedTab == Tab.Personal) validateSubmit(false)
        else if (selectedTab == Tab.Library) validateSubmit(true)
    }
    $: if (activityPath) onSelectAutoFire()

    const onActivitySelect = (activity) => {
        if (activity.upcoming_event_date && !activity.is_achievement) {
            raceDate = activity.upcoming_event_date.substring(0, 10)
        } else {
            raceDate = LocalDate.format(addDays(new Date(), 1))
        }
        activityPath = activity.path
        raceLogoUrl = activity.event_logo_url
    }
</script>

<!-- Select Tab -->
{#if selectedTab === Tab.Select}  
    <div class="flex flex-wrap justify-evenly gap-x-3 gap-y-6 flex-1 pb-6 md:pb-0">
        {#each selectButtons as option}
            <button class="flex flex-col text-center text-xerteal-600 justify-center items-center bg-xerteal-50 border border-xerteal-400 rounded aspect-[4/5] px-10 py-5 select-button transition-all shadow-lg shadow-black/20 hover:shadow-xl gap-6 xl:gap-8 2xl:gap-12 w-52 xl:w-60 2xl:w-72" on:click={() => selectedTab=option.tab}>
                <span class="text-xl xl:text-2xl 2xl:text-3xl font-bold ">{option.title}</span>
                <i class={`fa-regular text-xerteal-400 text-3xl xl:text-4xl 2xl:text-5xl ${option.icon}`}/>
                <span class="text-md xl:text-lg 2xl:text-xl">{option.description}</span>
            </button>
        {/each}
    </div>
{/if}


<!-- * Needs to be hidden rather than completely removed for Jquery to work -->
<div class={className} class:hidden={selectedTab === Tab.Select}>
    
    <button class="absolute top-[2px] right-[calc(100%-40px)] z-10 btn-xfai-icon border-0 text-[--color-xert-teal] bg-transparent hover:text-3xl rounded-md text-2xl" on:click={returnToSelect}>
        <i class="fa-solid fa-arrow-left" />
    </button>

    <!-- Input header bar -->
    {#if selectedTab === Tab.FileUpload}
        <div class="flex flex-wrap items-baseline gap-3 text-base">
            {#if validationError}
                <ProgramAchievabilityMessage status="error" statusMessage={validationError} />
            {/if}

            <div class ="flex gap-4 ml-auto">
                <button class="btn-xfai border-none text-white bg-[--color-xert-teal] hover:bg-[--color-xert-blue]" on:click={() => validateSubmit(false)}> Select </button>
            </div>
        </div>

        <div class="h-px bg-black/10"/>
    {/if}

    <!-- Xert Library -->
    {#if selectedTab === Tab.Library}
    <div class="flex flex-col justify-center gap-3">
        <ActivitiesDashLegacy on:select on:changeRace isRacePicker isRaceLibraryPicker dataSource='/activities/race-lib' onSelect={onActivitySelect} defaultSort={0} {plan} {mpaData} {targetSignature} {programAchievability} filterKey="racePersonalSelector" />
    </div>
    {/if}

    <!-- Personal Activities -->
    {#if selectedTab === Tab.Personal}
    <div class="flex flex-col justify-center gap-3">
        <ActivitiesDashLegacy on:select isRacePicker dataSource='/activities/dashboard' onSelect={onActivitySelect} filterKey="raceSelector" />
    </div>
    {/if}

    <!-- ! CURRENTLY HIDDEN / WIP -->
    <!-- {#if selectedTab === Tab.Squad}
    <div class="flex flex-col justify-center gap-3">
        <ActivitiesDashLegacy isCalendarPicker bind:selected_exercise bind:activityPath showOwner/>
    </div>
    {/if} -->

    <!-- Race Upload Tab -->
    <div class="grid gap-x-14 gap-y-12 items-start px-3 py-1 md:px-10 md:py-5" class:hidden={selectedTab !== Tab.FileUpload}>
        <div class="flex flex-col row-start-1">
            <span class="text-base">Race Date</span>
            <span class="text-slate-500 text-sm">Date you plan on completing the race on</span>
            <DatePicker class="mt-4 w-52 text-lg" min={minDate} bind:value={raceDate} required/>
        </div>
        <div class="flex flex-col row-start-2 sm:row-start-1">
            <span class="text-base">Rider Weight</span>
            <span class="text-slate-500 text-sm max-w-xs">Weight of the athlete that completed the race in your selected file</span>
            <NumberInput class="max-w-[8rem] mt-4" bind:numberInput={riderWeightInput} min=0 max=500  bind:value={riderWeight} unit={$userParams.weight_unit} required/>
        </div>
        <form id="race-file-form" class="col-span-2" bind:this={raceFileForm} method="post" enctype="multipart/form-data" on:submit|preventDefault={() => validateSubmit(false)}>
            <input bind:this={fileInput} type="file" name="race_file" class="race-file" required>
        </form>
    </div>
</div>


<style lang="scss">

    // .hover-left {
    //     position: absolute;
    //     right: calc(10px + 100%);
    //     top: 0px;
    // }

    .select-button {
        &:hover {
            transform: scale(1.05);
        }
    }

    :global(div.fileuploader-popup) {
      z-index: 9999;
    }
    // Base drag and drop theme copied from example:
    // https://innostudio.de/fileuploader/documentation/examples/drag-drop/
    // ? Does this need to be global
    :global {
      /* Root element */
      .fileuploader-theme-dragdrop.fileuploader {
        margin: 0;
        padding: 0;
        text-align: center;
        background: none;
        width: auto;  
      }
      /* input */
      .fileuploader-theme-dragdrop .fileuploader-input {
        display: block;
        padding: 16px;
        text-align: center;
  
        background: rgba(47, 112, 118, 0.05);
        border: 1px dashed #979797;
        box-sizing: border-box;
        border-radius: 3px;
      }
  
      .fileuploader-theme-dragdrop .fileuploader-input .fileuploader-input-button {
        // background: #2F7076;
        border-radius: 4px;
        margin: 0;
        padding: 0;
        width: 191px;
        height: 30px;
        > span {
          font-family: Roboto;
          font-style: normal;
          font-weight: normal;
          font-size: 16px;
          line-height: 19px;
          text-align: center;
          letter-spacing: 0.48px;
  
          color: #FFFFFF;
        }
  
      }
      .fileuploader-theme-dragdrop .fileuploader-input h3 {
        margin: 0;
        margin-bottom: 8px;
        padding: 0;
        background: none;
        border: none;
        border-radius: 0;
  
        font-family: Roboto;
        font-style: normal;
        font-weight: normal;
        font-size: 14px;
        line-height: 24px;
        text-align: center;
        letter-spacing: 0.113198px;
  
        color: #3D3D3D;
  
  
      }
      .fileuploader-theme-dragdrop .fileuploader-input p {
        margin: 0;
        padding: 0;
        color: #90a0bc;
        margin-bottom: 12px;
      }
  
      /* dragging state */
      .fileuploader-theme-dragdrop .fileuploader-input .fileuploader-input-inner > * {
          -webkit-transition: 500ms cubic-bezier(0.17, 0.67, 0, 1.01);
                  transition: 500ms cubic-bezier(0.17, 0.67, 0, 1.01);
      }
      .fileuploader-theme-dragdrop .fileuploader-input.fileuploader-dragging .fileuploader-input-inner > * {
        transform: translateY(18px);
        opacity: 0;
      }
      .fileuploader-theme-dragdrop .fileuploader-input.fileuploader-dragging .fileuploader-icon-main {
        transform: translateY(30px) scale(1.2);
        opacity: 0.6;
      }
      .fileuploader-theme-dragdrop .fileuploader-input.fileuploader-dragging .fileuploader-input-caption {
        transform: translateY(30px);
        opacity: 0.6;
      }
  
      .fileuploader-theme-dragdrop .fileuploader-items {
        max-height: 250px;
        overflow: hidden;
        
      }
    }
  </style>

